<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-heartbeat"></i> {{ $t('diagTitle') }}</h1>
        <p class="mb-0 text-muted">{{ $t('diagSubtitle') }}</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item">{{ $t('diagTitle') }}</li>
      </ul>
    </div>

    <div class="d-flex align-items-center mb-3">
      <span class="text-muted mr-2">{{ $t('diagPeriod') }}:</span>
      <select v-model="rangeKey" class="form-control form-control-sm" style="width:auto" @change="loadAll">
        <option value="today">{{ $t('diagToday') }}</option>
        <option value="7d">{{ $t('diag7d') }}</option>
        <option value="30d">{{ $t('diag30d') }}</option>
      </select>
      <button class="btn btn-sm btn-outline-secondary ml-2" :disabled="loading" @click="loadAll">
        <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i> {{ $t('diagRefresh') }}
      </button>
      <span v-if="lastUpdatedStr" class="text-muted small ml-3"><i class="fa fa-clock-o"></i> {{ $t('diagUpdatedAt') }} {{ lastUpdatedStr }} <span class="text-success">({{ $t('diagAuto') }})</span></span>
    </div>

    <!-- Giải thích chỉ số (gập/mở) -->
    <div class="card mb-3">
      <div class="card-header diag-legend-toggle" @click="showLegend = !showLegend">
        <i class="fa fa-info-circle text-info"></i> {{ $t('diagLegend') }}
        <i class="fa float-right mt-1" :class="showLegend ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>
      <div class="card-body py-2" v-show="showLegend">
        <p class="text-muted small mb-2">{{ $t('diagLegendColors') }}</p>
        <div class="row">
          <div class="col-md-6 col-lg-4 py-1" v-for="(it, i) in legendItems" :key="i">
            <b>{{ it.label }}</b>
            <div class="text-muted small">{{ it.hint }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Thẻ tổng quan -->
    <div class="row diag-cards mb-3">
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-user-circle text-secondary"></i>
          <div class="stat-value">{{ overview.agents || 0 }}</div>
          <div class="stat-label">{{ $t('diagAgents') }}</div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-server" :class="statusCls(levelLatency(overview.apiP95, 800, 2000))"></i>
          <div class="stat-value" :class="statusCls(levelLatency(overview.apiP95, 800, 2000))">{{ fmtMs(overview.apiP95 || 0) }}</div>
          <div class="stat-label" :title="$t('diagApiHint')">{{ $t('diagApi') }}</div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-microchip" :class="statusCls(levelLatency(overview.apiTtfb, 300, 800))"></i>
          <div class="stat-value" :class="statusCls(levelLatency(overview.apiTtfb, 300, 800))">{{ fmtMs(overview.apiTtfb || 0) }}</div>
          <div class="stat-label" :title="$t('diagTtfbHint')">{{ $t('diagTtfb') }}</div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-cloud-download" :class="statusCls(levelLatency(overview.apiDownload, 1000, 3000))"></i>
          <div class="stat-value" :class="statusCls(levelLatency(overview.apiDownload, 1000, 3000))">{{ fmtMs(overview.apiDownload || 0) }}</div>
          <div class="stat-label" :title="$t('diagDownloadHint')">{{ $t('diagDownload') }}</div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-phone" :class="statusCls(levelMos(overview.mos))"></i>
          <div class="stat-value" :class="statusCls(levelMos(overview.mos))">{{ overview.mos || '—' }}</div>
          <div class="stat-label" :title="$t('diagMosHint')">{{ $t('diagMos') }}</div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-plug" :class="overview.reconnects ? 'text-warning' : 'text-success'"></i>
          <div class="stat-value">{{ overview.reconnects || 0 }}</div>
          <div class="stat-label" :title="$t('diagReconnectsHint')">{{ $t('diagReconnects') }}</div>
        </div></div>
      </div>
      <div class="col-md">
        <div class="card stat-card h-100"><div class="card-body text-center">
          <i class="fa fa-shield" :class="overview.firewallBlocked ? 'text-danger' : 'text-success'"></i>
          <div class="stat-value" :class="overview.firewallBlocked ? 'text-danger' : ''">{{ overview.firewallBlocked || 0 }}</div>
          <div class="stat-label" :title="$t('diagFirewallHint')">{{ $t('diagFirewall') }}</div>
        </div></div>
      </div>
    </div>

    <!-- Khách hàng -->
    <div class="card mb-3">
      <div class="card-header"><i class="fa fa-building text-primary"></i> {{ $t('diagClientsTitle') }}</div>
      <div class="table-responsive">
        <table class="table table-hover mb-0 align-middle">
          <thead class="thead-light"><tr>
            <th>{{ $t('diagClient') }}</th>
            <th class="text-center">{{ $t('diagAgents') }}</th>
            <th class="text-center" :title="$t('diagApiHint')">{{ $t('diagApi') }}</th>
            <th class="text-center" :title="$t('diagTtfbHint')">{{ $t('diagTtfb') }}</th>
            <th class="text-center" :title="$t('diagDownloadHint')">{{ $t('diagDownload') }}</th>
            <th class="text-center" :title="$t('diagMosHint')">{{ $t('diagMos') }}</th>
            <th class="text-center">{{ $t('diagReconnects') }}</th>
            <th class="text-center">{{ $t('diagFirewall') }}</th>
            <th></th>
          </tr></thead>
          <tbody>
            <tr v-for="c in clients" :key="c._id">
              <td><b>{{ c.clientName }}</b><small v-if="c.domain" class="text-muted d-block">{{ c.domain }}</small></td>
              <td class="text-center">{{ c.agents }}</td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelLatency(c.apiP95, 800, 2000))">{{ fmtMs(c.apiP95) }} · {{ statusWord(levelLatency(c.apiP95, 800, 2000)) }}</span></td>
              <td class="text-center"><span :class="statusCls(levelLatency(c.apiTtfb, 300, 800))">{{ fmtMs(c.apiTtfb) }}</span></td>
              <td class="text-center"><span :class="statusCls(levelLatency(c.apiDownload, 1000, 3000))">{{ fmtMs(c.apiDownload) }}</span></td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelMos(c.mos))">{{ c.mos || '—' }}<template v-if="c.mos"> · {{ statusWord(levelMos(c.mos)) }}</template></span></td>
              <td class="text-center"><span :class="c.reconnects ? 'text-warning font-weight-bold' : 'text-muted'">{{ c.reconnects }}</span></td>
              <td class="text-center"><span :class="c.firewallBlocked ? 'text-danger font-weight-bold' : 'text-muted'">{{ c.firewallBlocked }}</span></td>
              <td class="text-right">
                <button class="btn btn-sm btn-outline-primary" @click="selectClient(c)"><i class="fa fa-users"></i> {{ $t('diagViewAgents') }}</button>
                <button v-if="canDelete" class="btn btn-sm btn-outline-danger is-border-1 ml-1" :title="$t('diagDelete')" @click="deleteClient(c)"><i class="fa fa-trash"></i></button>
              </td>
            </tr>
            <tr v-if="!clients.length"><td colspan="9" class="text-center text-muted py-4"><i class="fa fa-inbox fa-2x d-block mb-2"></i>{{ $t('diagEmpty') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Nhân viên của khách hàng -->
    <div class="card mb-3" v-if="selectedClient">
      <div class="card-header"><i class="fa fa-users text-primary"></i> {{ $t('diagAgentsOf') }} <b>{{ selectedClient.clientName }}</b><small v-if="selectedClient.domain" class="text-muted"> · {{ selectedClient.domain }}</small></div>
      <div class="table-responsive">
        <table class="table table-hover mb-0 align-middle">
          <thead class="thead-light"><tr>
            <th>{{ $t('diagAgent') }}</th>
            <th class="text-center" :title="$t('diagApiHint')">{{ $t('diagApi') }}</th>
            <th class="text-center" :title="$t('diagTtfbHint')">{{ $t('diagTtfb') }}</th>
            <th class="text-center" :title="$t('diagDownloadHint')">{{ $t('diagDownload') }}</th>
            <th class="text-center">{{ $t('diagMos') }}</th>
            <th class="text-center" :title="$t('diagRealtimeHint')">{{ $t('diagRealtime') }}</th>
            <th class="text-center">{{ $t('diagReconnects') }}</th>
            <th class="text-center" :title="$t('diagSmoothHint')">{{ $t('diagSmooth') }}</th>
            <th class="text-center">{{ $t('diagMemory') }}</th>
            <th></th>
          </tr></thead>
          <tbody>
            <tr v-for="u in users" :key="u._id">
              <td><b>{{ u.name }}</b> <small class="text-muted" v-if="u.ext">· {{ u.ext }}</small></td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelLatency(u.apiP95, 800, 2000))">{{ fmtMs(u.apiP95) }}</span></td>
              <td class="text-center"><span :class="statusCls(levelLatency(u.apiTtfb, 300, 800))">{{ fmtMs(u.apiTtfb) }}</span></td>
              <td class="text-center"><span :class="statusCls(levelLatency(u.apiDownload, 1000, 3000))">{{ fmtMs(u.apiDownload) }}</span></td>
              <td class="text-center"><span class="badge" :class="badgeCls(levelMos(u.mos))">{{ u.mos || '—' }}</span></td>
              <td class="text-center">{{ fmtMs(u.socketRtt) }}</td>
              <td class="text-center"><span :class="u.reconnects ? 'text-warning font-weight-bold' : 'text-muted'">{{ u.reconnects }}</span></td>
              <td class="text-center"><span :class="u.fps && u.fps < 30 ? 'text-danger font-weight-bold' : ''">{{ u.fps || '—' }}<small v-if="u.fps"> fps</small></span></td>
              <td class="text-center">{{ u.heap ? u.heap + ' MB' : '—' }}</td>
              <td class="text-right">
                <button class="btn btn-sm btn-outline-secondary" @click="selectUser(u)"><i class="fa fa-history"></i> {{ $t('diagHistory') }}</button>
                <button v-if="canDiagnose" class="btn btn-sm btn-outline-info is-border-1 ml-1" @click="openDiagnosis(u)"><i class="fa fa-magic"></i> {{ $t('diagDiagnose') }}</button>
                <button v-if="canDelete" class="btn btn-sm btn-outline-danger is-border-1 ml-1" :title="$t('diagDelete')" @click="deleteUser(u)"><i class="fa fa-trash"></i></button>
              </td>
            </tr>
            <tr v-if="!users.length"><td colspan="10" class="text-center text-muted py-4">{{ $t('diagEmpty') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Lịch sử của nhân viên -->
    <div class="card" v-if="selectedUser">
      <div class="card-header d-flex align-items-center">
        <span><i class="fa fa-history text-primary"></i> {{ $t('diagHistoryOf') }} <b>{{ selectedUser.name }}</b></span>
        <button v-if="canDelete" class="btn btn-sm btn-outline-danger is-border-1 ml-auto" @click="deleteUser(selectedUser)"><i class="fa fa-trash"></i> {{ $t('diagDelete') }}</button>
      </div>
      <div class="table-responsive" style="max-height:420px;overflow:auto">
        <table class="table table-sm mb-0">
          <thead class="thead-light"><tr>
            <th>{{ $t('diagTime') }}</th>
            <th :title="$t('diagApiHint')">{{ $t('diagApi') }}</th>
            <th :title="$t('diagTtfbHint')">{{ $t('diagTtfb') }}</th>
            <th :title="$t('diagDownloadHint')">{{ $t('diagDownload') }}</th>
            <th>{{ $t('diagSlowEp') }}</th>
            <th :title="$t('diagPageHint')">{{ $t('diagPage') }}</th>
            <th :title="$t('diagRealtimeHint')">{{ $t('diagRealtime') }}</th>
            <th>{{ $t('diagMos') }}</th>
            <th>{{ $t('diagSmooth') }}</th>
            <th :title="$t('diagLongTaskHint')">{{ $t('diagLongTask') }}</th>
            <th>{{ $t('diagMemory') }}</th>
            <th>{{ $t('diagNetwork') }}</th>
            <th>{{ $t('diagSource') }}</th>
          </tr></thead>
          <tbody>
            <tr v-for="(d, i) in timeline" :key="i">
              <td class="text-nowrap">{{ fmtTime(d.bucketTime) }}</td>
              <td v-if="d.hidden" class="text-muted" :title="$t('diagHiddenHint')"><i class="fa fa-moon-o"></i> {{ fmtMs(d.api && d.api.p95) }}</td>
              <td v-else :class="statusCls(levelLatency(d.api && d.api.p95, 800, 2000))">{{ fmtMs(d.api && d.api.p95) }}</td>
              <td :class="statusCls(levelLatency(d.api && d.api.ttfb, 300, 800))">{{ fmtMs(d.api && d.api.ttfb) }}</td>
              <td :class="statusCls(levelLatency(d.api && d.api.download, 1000, 3000))">{{ fmtMs(d.api && d.api.download) }}</td>
              <td><small class="text-muted">{{ (d.api && d.api.slowestEndpoint) || '—' }}</small></td>
              <td><small class="text-muted">{{ d.route || '—' }}</small></td>
              <td>{{ fmtMs(d.socket && d.socket.rtt) }}</td>
              <td :class="statusCls(levelMos(d.rtp && d.rtp.mos))">{{ d.rtp && d.rtp.mos != null ? d.rtp.mos : '—' }}</td>
              <td :class="d.fe && d.fe.fps != null && d.fe.fps < 30 ? 'text-danger' : ''">{{ d.fe && d.fe.fps != null ? d.fe.fps + ' fps' : '—' }}</td>
              <td :class="d.fe && d.fe.longTaskMaxMs >= 1000 ? 'text-danger' : ''" :title="$t('diagLongTaskHint')">{{ longTaskLabel(d) }}</td>
              <td>{{ d.fe && d.fe.heapUsedMb != null ? d.fe.heapUsedMb + ' MB' : '—' }}</td>
              <td><small>{{ netLabel(d) }}{{ d.region ? ' · ' + d.region : '' }}</small></td>
              <td><span class="badge badge-light">{{ srcLabel(d.source) }}</span></td>
            </tr>
            <tr v-if="!timeline.length"><td colspan="13" class="text-center text-muted py-4">{{ $t('diagEmpty') }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'AdminDiagnostic',
  data() {
    return {
      rangeKey: '7d', loading: false, lastUpdatedStr: '', showLegend: false,
      overview: {}, clients: [],
      selectedClient: null, users: [],
      selectedUser: null, timeline: [],
    };
  },
  computed: {
    // Xoá là thao tác phá huỷ → chỉ owner/admin/tech (khớp role.check ở backend) mới thấy nút.
    canDelete() {
      const role = this.$store.state.user && this.$store.state.user.role;
      return ['owner', 'admin', 'tech'].includes(role);
    },
    // Chẩn đoán AI tốn chi phí LLM → cùng nhóm quyền với xoá.
    canDiagnose() { return this.canDelete; },
    // Chú giải toàn bộ cột — tái dùng chính các tooltip (*Hint) để 1 nguồn nội dung duy nhất.
    legendItems() {
      return [
        { label: this.$t('diagApi'), hint: this.$t('diagApiHint') },
        { label: this.$t('diagTtfb'), hint: this.$t('diagTtfbHint') },
        { label: this.$t('diagDownload'), hint: this.$t('diagDownloadHint') },
        { label: this.$t('diagSlowEp'), hint: this.$t('diagSlowEpHint') },
        { label: this.$t('diagRealtime'), hint: this.$t('diagRealtimeHint') },
        { label: this.$t('diagMos'), hint: this.$t('diagMosHint') },
        { label: this.$t('diagReconnects'), hint: this.$t('diagReconnectsHint') },
        { label: this.$t('diagSmooth'), hint: this.$t('diagSmoothHint') },
        { label: this.$t('diagLongTask'), hint: this.$t('diagLongTaskHint') },
        { label: this.$t('diagMemory'), hint: this.$t('diagMemoryHint') },
        { label: this.$t('diagNetwork'), hint: this.$t('diagNetworkHint') },
        { label: this.$t('diagFirewall'), hint: this.$t('diagFirewallHint') },
        { label: this.$t('diagSource'), hint: this.$t('diagSourceHint') },
      ];
    },
  },
  mounted() {
    this.loadAll();
    // Auto-refresh mỗi 10s — chỉ làm mới tổng quan + danh sách, KHÔNG reset drill-down.
    this._refreshTimer = setInterval(() => this.refreshTop(), 10000);
  },
  beforeDestroy() {
    if (this._refreshTimer) { clearInterval(this._refreshTimer); this._refreshTimer = null; }
  },
  methods: {
    async refreshTop() {
      const { from, to } = this.computeRange();
      try {
        const [ov, cl] = await Promise.all([
          this.$service.get('/diagnostic/overview', { params: { from, to } }),
          this.$service.get('/diagnostic/clients', { params: { from, to } }),
        ]);
        this.overview = ov.data || {};
        this.clients = (cl.data && cl.data.docs) || [];
        this.lastUpdatedStr = new Date().toLocaleTimeString('vi-VN');
      } catch (e) { /* giữ dữ liệu cũ nếu lỗi */ }
    },
    computeRange() {
      const end = Date.now();
      let start;
      if (this.rangeKey === 'today') { const d = new Date(); d.setHours(0, 0, 0, 0); start = d.getTime(); }
      else if (this.rangeKey === '30d') start = end - 30 * 24 * 3600 * 1000;
      else start = end - 7 * 24 * 3600 * 1000;
      return { from: start, to: end };
    },
    async loadAll() {
      this.loading = true;
      this.selectedClient = null; this.users = []; this.selectedUser = null; this.timeline = [];
      const { from, to } = this.computeRange();
      try {
        const [ov, cl] = await Promise.all([
          this.$service.get('/diagnostic/overview', { params: { from, to } }),
          this.$service.get('/diagnostic/clients', { params: { from, to } }),
        ]);
        this.overview = ov.data || {};
        this.clients = (cl.data && cl.data.docs) || [];
        this.lastUpdatedStr = new Date().toLocaleTimeString('vi-VN');
      } catch (e) { this.clients = []; this.overview = {}; }
      finally { this.loading = false; }
    },
    async selectClient(c) {
      this.selectedClient = c; this.users = []; this.selectedUser = null; this.timeline = [];
      const { from, to } = this.computeRange();
      try {
        const r = await this.$service.get(`/diagnostic/client/${c._id}/users`, { params: { from, to } });
        this.users = (r.data && r.data.docs) || [];
      } catch (e) { this.users = []; }
    },
    async selectUser(u) {
      this.selectedUser = u; this.timeline = [];
      const { from, to } = this.computeRange();
      try {
        const r = await this.$service.get(`/diagnostic/user/${u._id}`, { params: { from, to } });
        this.timeline = (r.data && r.data.docs) || [];
      } catch (e) { this.timeline = []; }
    },
    // Xoá datapoint của 1 KH trong khoảng đang chọn → reload toàn trang.
    async deleteClient(c) {
      const r = await this.$swal({ icon: 'warning', text: this.$t('diagDelClientConfirm', { name: c.clientName }), showCancelButton: true, confirmButtonText: this.$t('diagDelete') });
      if (!r.value) return;
      const loader = this.$loading.show();
      const { from, to } = this.computeRange();
      try {
        const res = await this.$service.delete(`/diagnostic/client/${c._id}`, { params: { from, to } });
        const n = (res.data && res.data.deleted) || 0;
        await this.loadAll();
        this.$swal({ icon: 'success', text: this.$t('diagDeleted', { n }), timer: 2000, showConfirmButton: false });
      } catch (e) { this.$swal({ icon: 'error', text: this.$t('diagDelFail') }); }
      finally { loader.hide(); }
    },
    // Xoá datapoint của 1 nhân viên trong khoảng đang chọn → reload lại lịch sử + danh sách trên.
    async deleteUser(u) {
      const r = await this.$swal({ icon: 'warning', text: this.$t('diagDelUserConfirm', { name: u.name }), showCancelButton: true, confirmButtonText: this.$t('diagDelete') });
      if (!r.value) return;
      const loader = this.$loading.show();
      const { from, to } = this.computeRange();
      try {
        const res = await this.$service.delete(`/diagnostic/user/${u._id}`, { params: { from, to } });
        const n = (res.data && res.data.deleted) || 0;
        this.timeline = [];
        if (this.selectedClient) await this.selectClient(this.selectedClient);
        await this.refreshTop();
        this.$swal({ icon: 'success', text: this.$t('diagDeleted', { n }), timer: 2000, showConfirmButton: false });
      } catch (e) { this.$swal({ icon: 'error', text: this.$t('diagDelFail') }); }
      finally { loader.hide(); }
    },
    // Mở chẩn đoán AI: ưu tiên xem bản đã lưu (không tốn LLM); chưa có → phân tích mới.
    async openDiagnosis(u) {
      const loader = this.$loading.show();
      let doc = null;
      try {
        const res = await this.$service.get(`/diagnostic/user/${u._id}/analysis`);
        doc = res.data && res.data.doc;
      } catch (e) { /* không có bản cũ → phân tích mới */ }
      finally { loader.hide(); }
      if (doc && doc.result) this.showDiagResult(doc, u);
      else this.runAnalyze(u);
    },
    // Gọi LLM phân tích mới (tốn chi phí) → lưu + hiển thị.
    async runAnalyze(u) {
      const loader = this.$loading.show();
      const { from, to } = this.computeRange();
      try {
        const res = await this.$service.post(`/diagnostic/user/${u._id}/analyze`, null, { params: { from, to, rangeKey: this.rangeKey } });
        const d = res.data || {};
        if (d.empty) { this.$swal({ icon: 'info', text: this.$t('diagNoData') }); return; }
        if (d.doc && d.doc.result) this.showDiagResult(d.doc, u);
        else this.$swal({ icon: 'error', text: this.$t('diagAiFail') });
      } catch (e) {
        const err = e.response && e.response.data && e.response.data.error;
        this.$swal({ icon: 'error', text: err === 'OPENAI_KEY_MISSING' ? this.$t('diagAiKeyMissing') : this.$t('diagAiFail') });
      } finally { loader.hide(); }
    },
    showDiagResult(doc, u) {
      const who = doc.createdBy && doc.createdBy.name ? ' · ' + doc.createdBy.name : '';
      const meta = `${this.$t('diagAnalyzedAt')} ${this.fmtTime(doc.createTime)}${who}`;
      this.$swal({
        title: `${this.$t('diagDiagnose')} — ${u.name}`,
        html: `<div style="white-space:pre-wrap;text-align:left;max-height:50vh;overflow:auto;font-size:.9rem;line-height:1.5">${this.escapeHtml(doc.result)}</div>`
          + `<div style="text-align:left;font-size:.78rem;color:#6c757d;margin-top:.6rem">${this.escapeHtml(meta)}</div>`,
        showCancelButton: true,
        confirmButtonText: this.$t('diagReanalyze'),
        cancelButtonText: this.$t('diagClose'),
        width: 640,
      }).then(r => { if (r.value) this.runAnalyze(u); });
    },
    escapeHtml(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); },
    fmtTime(ms) { try { return new Date(ms).toLocaleString('vi-VN'); } catch (e) { return ms; } },
    // Hiển thị thời lượng (ms): <1s giữ "ms", ≥1s đổi sang "x.xs" cho dễ đọc (VD 11394ms → 11.4s).
    fmtMs(v) {
      if (v == null || v === '') return '—';
      const n = Number(v);
      if (!isFinite(n)) return '—';
      return n < 1000 ? n + 'ms' : (n / 1000).toFixed(1) + 's';
    },
    srcLabel(s) { return s === 'manual' ? this.$t('diagSrcManual') : this.$t('diagSrcAuto'); },
    // Hiển thị "count · max" — max là long task DÀI NHẤT: ≥1s = freeze JS thật; latency cao mà max nhỏ = nghi tab nền (ghost)
    longTaskLabel(d) {
      if (!d.fe || d.fe.longTaskCount == null) return '—';
      const n = d.fe.longTaskCount;
      const max = d.fe.longTaskMaxMs;
      if (max == null || max <= 0) return String(n);
      return `${n} · ${max < 1000 ? max + 'ms' : (max / 1000).toFixed(1) + 's'}`;
    },
    // netMedium = loại mạng vật lý THẬT (wifi/cellular...) — chỉ Chrome Android/ChromeOS expose.
    // Desktop KHÔNG biết WiFi/4G, chỉ có netType (effectiveType) = TỐC ĐỘ ước tính (slow-2g/2g/3g/4g; '4g' kể cả WiFi/Ethernet).
    // → Khi thiếu medium, hiện CHỮ TỐC ĐỘ thuần (Nhanh/Trung bình/Rất chậm), KHÔNG hiện "4G" gây hiểu nhầm loại mạng.
    netLabel(d) {
      const MEDIUM = { wifi: 'WiFi', cellular: this.$t('diagNetCellular'), ethernet: 'Ethernet', wimax: 'WiMAX', bluetooth: 'Bluetooth' };
      if (d.netMedium) return MEDIUM[d.netMedium] || d.netMedium;
      const SPEED = { 'slow-2g': this.$t('diagNetSlow'), '2g': this.$t('diagNetSlow'), '3g': this.$t('diagNetMedium'), '4g': this.$t('diagNetFast') };
      if (d.netType) return SPEED[String(d.netType).toLowerCase()] || '—';
      return '—';
    },
    // mức độ: latency cao = xấu
    levelLatency(v, good, warn) { if (v == null) return null; if (v <= good) return 'good'; if (v <= warn) return 'warn'; return 'bad'; },
    levelMos(v) { if (v == null || !v) return null; if (v >= 4) return 'good'; if (v >= 3) return 'warn'; return 'bad'; },
    statusWord(l) { return { good: this.$t('diagGood'), warn: this.$t('diagWarn'), bad: this.$t('diagBad') }[l] || ''; },
    statusCls(l) { return { good: 'text-success', warn: 'text-warning', bad: 'text-danger' }[l] || 'text-muted'; },
    badgeCls(l) { return { good: 'badge-success', warn: 'badge-warning', bad: 'badge-danger' }[l] || 'badge-light'; },
  },
  i18n: {
    messages: {
      vi: {
        diagTitle: 'Chẩn đoán hiệu năng',
        diagSubtitle: 'Theo dõi sức khoẻ kết nối & cuộc gọi của khách hàng để biết ai đang gặp vấn đề và vì sao.',
        diagPeriod: 'Khoảng thời gian', diagToday: 'Hôm nay', diag7d: '7 ngày qua', diag30d: '30 ngày qua', diagRefresh: 'Tải lại',
        diagUpdatedAt: 'Cập nhật lúc', diagAuto: 'tự động 10s',
        diagAgents: 'Nhân viên',
        diagApi: 'Độ trễ hệ thống', diagApiHint: 'TỔNG thời gian 1 yêu cầu (p95) = mạng khách + máy chủ + tải dữ liệu. Số cao CHƯA chắc do máy chủ — đối chiếu cột Xử lý máy chủ & Tải dữ liệu để biết tầng nào chậm.',
        diagMos: 'Chất lượng gọi', diagMosHint: 'Điểm chất lượng âm thanh cuộc gọi (1–5). ≥4 tốt, <3 kém.',
        diagReconnects: 'Rớt kết nối', diagReconnectsHint: 'Số lần mất kết nối thời gian thực (tin nhắn/cuộc gọi đến trễ).',
        diagFirewall: 'Bị chặn tường lửa', diagFirewallHint: 'Số lượt phát hiện mạng/tường lửa chặn cổng gọi (UDP/TURN) → mất tiếng.',
        diagRealtime: 'Độ trễ thời gian thực', diagRealtimeHint: 'Tốc độ tin nhắn/cuộc gọi ĐẾN hiện lên máy (ping WebSocket) — khác Độ trễ hệ thống (tải/lưu dữ liệu qua API). Càng thấp càng nhạy.',
        diagSmooth: 'Độ mượt', diagSmoothHint: 'Khung hình/giây của giao diện (FPS). <30 = giật.',
        diagLongTask: 'Long task', diagLongTaskHint: 'count · max — số tác vụ JS >50ms làm đơ UI và tác vụ DÀI NHẤT. Max ≥1s = freeze JS thật (fix gấp). Latency cao mà max nhỏ + tab nền (🌙) = số đo ẢO, không phải lỗi.',
        diagHiddenHint: 'Tab chạy nền/máy ngủ trong lúc đo → độ trễ này gồm cả thời gian nền nên KHÔNG đáng tin (số ẢO), không phải request chậm thật.',
        diagMemory: 'Bộ nhớ',
        diagTtfb: 'Xử lý máy chủ', diagTtfbHint: 'Thời gian MÁY CHỦ Callio xử lý & bắt đầu trả dữ liệu (TTFB). Cao = máy chủ chậm. Thấp mà tổng cao = lỗi do mạng/tải, KHÔNG phải máy chủ.',
        diagDownload: 'Tải dữ liệu', diagDownloadHint: 'Thời gian tải dữ liệu trả về (mạng/băng thông phía khách). Cao mà Xử lý máy chủ thấp = nghẽn mạng khách, không phải máy chủ Callio.',
        diagSlowEp: 'Chức năng chậm nhất', diagSlowEpHint: 'API (tính năng) chậm nhất trong khoảng đo — đã bóc ID/PII. Giúp khoanh vùng tính năng nặng cần tối ưu, VD /customer, /notification/count.',
        diagPage: 'Trang', diagPageHint: 'Màn hình/trang trong app mà nhân viên ĐANG ĐỨNG lúc đo (đã bóc ID/PII), VD /customer/:id/overview. Khác Chức năng chậm nhất (API): cho biết nhân viên đang ở ĐÂU trong app khi gặp chậm.',
        diagMemoryHint: 'Bộ nhớ heap JS trình duyệt đang dùng (MB). Tăng dần theo phiên làm việc dài = nghi rò rỉ bộ nhớ, nên tải lại trang.',
        diagNetworkHint: 'Loại/tốc độ mạng máy nhân viên. Desktop chỉ ước tính tốc độ (Nhanh/Trung bình/Rất chậm); WiFi/Di động chỉ hiện trên Chrome Android.',
        diagSourceHint: 'Nguồn dữ liệu: Tự động = thu ngầm trong lúc làm việc; Nhân viên gửi = nhân viên bấm nút "Gửi Admin".',
        diagLegend: 'Giải thích chỉ số',
        diagLegendColors: 'Màu sắc: xanh = tốt · vàng = cảnh báo · đỏ = kém. Biểu tượng 🌙 = tab chạy nền lúc đo nên số đo không đáng tin (số ẢO).',
        diagNetwork: 'Tốc độ mạng', diagSource: 'Nguồn', diagSrcManual: 'Nhân viên gửi', diagSrcAuto: 'Tự động',
        diagNetCellular: 'Di động', diagNetFast: 'Nhanh', diagNetMedium: 'Trung bình', diagNetSlow: 'Rất chậm',
        diagTime: 'Thời gian',
        diagClientsTitle: 'Khách hàng cần chú ý', diagClient: 'Khách hàng', diagViewAgents: 'Xem nhân viên',
        diagAgentsOf: 'Nhân viên của', diagAgent: 'Nhân viên', diagHistory: 'Lịch sử', diagHistoryOf: 'Lịch sử của',
        diagGood: 'Tốt', diagWarn: 'Cảnh báo', diagBad: 'Kém', diagEmpty: 'Chưa có dữ liệu trong khoảng thời gian này',
        diagDelete: 'Xoá', diagDelFail: 'Xoá thất bại',
        diagDeleted: 'Đã xoá {n} bản ghi',
        diagDelClientConfirm: 'Xoá toàn bộ dữ liệu chẩn đoán của "{name}" trong khoảng thời gian đang chọn?',
        diagDelUserConfirm: 'Xoá toàn bộ dữ liệu chẩn đoán của "{name}" trong khoảng thời gian đang chọn?',
        diagDiagnose: 'Chẩn đoán AI', diagReanalyze: 'Phân tích lại', diagClose: 'Đóng',
        diagAnalyzedAt: 'Phân tích lúc',
        diagNoData: 'Không có dữ liệu để phân tích trong khoảng này',
        diagAiFail: 'Phân tích AI thất bại, thử lại sau',
        diagAiKeyMissing: 'Chưa cấu hình OpenAI key cho AI',
      },
      en: {
        diagTitle: 'Performance diagnostic',
        diagSubtitle: 'Monitor customers’ connection & call health to see who has issues and why.',
        diagPeriod: 'Period', diagToday: 'Today', diag7d: 'Last 7 days', diag30d: 'Last 30 days', diagRefresh: 'Reload',
        diagUpdatedAt: 'Updated at', diagAuto: 'auto 10s',
        diagAgents: 'Agents',
        diagApi: 'System latency', diagApiHint: 'TOTAL time of one request (p95) = client network + server + data download. A high number is NOT necessarily the server — compare with Server time & Download.',
        diagMos: 'Call quality', diagMosHint: 'Audio quality score (1–5). ≥4 good, <3 poor.',
        diagReconnects: 'Disconnects', diagReconnectsHint: 'Real-time disconnect count (delayed messages/calls).',
        diagFirewall: 'Firewall blocked', diagFirewallHint: 'Times network/firewall blocked call media (UDP/TURN).',
        diagRealtime: 'Realtime latency', diagRealtimeHint: 'How fast incoming messages/calls appear (WebSocket ping) — different from System latency (loading/saving via API). Lower is better.',
        diagSmooth: 'Smoothness', diagSmoothHint: 'UI frames per second (FPS). <30 = janky.',
        diagLongTask: 'Long task', diagLongTaskHint: 'count · max — number of JS tasks >50ms freezing the UI and the LONGEST one. Max ≥1s = real JS freeze (fix it). High latency but small max + backgrounded tab (🌙) = bogus measurement, not a real issue.',
        diagHiddenHint: 'Tab was backgrounded / device asleep during measurement → this latency includes idle background time, so it is NOT trustworthy (bogus), not a genuinely slow request.',
        diagMemory: 'Memory',
        diagTtfb: 'Server time', diagTtfbHint: 'Time the Callio SERVER spends processing & starting to respond (TTFB). High = slow server. Low while total is high = network/download issue, NOT the server.',
        diagDownload: 'Download', diagDownloadHint: 'Time to download the response (client network/bandwidth). High while Server time is low = client network congestion, not the Callio server.',
        diagSlowEp: 'Slowest function', diagSlowEpHint: 'Slowest API (function) in the period — IDs/PII stripped. Helps pinpoint heavy features to optimize, e.g. /customer, /notification/count.',
        diagPage: 'Page', diagPageHint: 'The app screen/page the agent was ON during measurement (IDs/PII stripped), e.g. /customer/:id/overview. Unlike Slowest function (API): shows WHERE in the app the agent was when it got slow.',
        diagMemoryHint: 'Browser JS heap in use (MB). Growing over a long session suggests a memory leak; reload the page.',
        diagNetworkHint: 'The agent device network type/speed. Desktop only estimates speed (Fast/Medium/Very slow); WiFi/Cellular shows only on Chrome Android.',
        diagSourceHint: 'Data source: Automatic = collected silently while working; Sent by agent = the agent clicked "Send to Admin".',
        diagLegend: 'Metric guide',
        diagLegendColors: 'Colors: green = good · yellow = warning · red = poor. The 🌙 icon = tab was backgrounded during measurement, so the value is not trustworthy.',
        diagNetwork: 'Network speed', diagSource: 'Source', diagSrcManual: 'Sent by agent', diagSrcAuto: 'Automatic',
        diagNetCellular: 'Cellular', diagNetFast: 'Fast', diagNetMedium: 'Medium', diagNetSlow: 'Very slow',
        diagTime: 'Time',
        diagClientsTitle: 'Clients to watch', diagClient: 'Client', diagViewAgents: 'View agents',
        diagAgentsOf: 'Agents of', diagAgent: 'Agent', diagHistory: 'History', diagHistoryOf: 'History of',
        diagGood: 'Good', diagWarn: 'Warning', diagBad: 'Poor', diagEmpty: 'No data in this period',
        diagDelete: 'Delete', diagDelFail: 'Delete failed',
        diagDeleted: 'Deleted {n} records',
        diagDelClientConfirm: 'Delete all diagnostic data of "{name}" in the selected period?',
        diagDelUserConfirm: 'Delete all diagnostic data of "{name}" in the selected period?',
        diagDiagnose: 'AI diagnose', diagReanalyze: 'Re-analyze', diagClose: 'Close',
        diagAnalyzedAt: 'Analyzed at',
        diagNoData: 'No data to analyze in this period',
        diagAiFail: 'AI analysis failed, try again later',
        diagAiKeyMissing: 'OpenAI key not configured',
      },
    },
  },
};
</script>

<style scoped>
.diag-cards .stat-card { border: none; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); border-radius: .5rem; }
.diag-cards .stat-card .fa { font-size: 1.4rem; opacity: .85; }
.diag-cards .stat-value { font-size: 1.6rem; font-weight: 700; line-height: 1.2; margin-top: .25rem; }
.diag-cards .stat-value small { font-size: .9rem; font-weight: 400; }
.diag-cards .stat-label { font-size: .8rem; color: #6c757d; }
.card-header { font-weight: 600; }
.diag-legend-toggle { cursor: pointer; user-select: none; }
.diag-legend-toggle:hover { background: #f8f9fa; }
.table thead th { font-size: .82rem; text-transform: none; border-top: none; }
</style>
